import { useEffect, useState } from 'react';
import { FieldInputProps, FormikContextType } from 'formik';
import { TextCheckbox } from '../typography/typography.styles';
import {
  CheckboxContainer,
  CheckboxContent,
  CheckboxWrapper,
} from './checkbox.styles';
import { ReactComponent as CheckboxIcon } from '../../assets/checkbox-icon.svg';
import InputMessage from '../input-message/input-message.component';
import { TValidationMethod } from '../../utils/forms/forms.utils';
import { useFormField } from '../../hooks/use-form-field.hook';

interface Props<FormValuesType> {
  name: string;
  children: React.ReactNode;
  tabIndex?: number;
  isChecked?: boolean;
  disabled?: boolean;
  marginBottom?: string;
  triggerValidation?: boolean;
  validationMethod?: TValidationMethod;
  onChecked?: (
    field: FieldInputProps<boolean>,
    formikContext: FormikContextType<FormValuesType>
  ) => void;
}

function FormCheckbox<FormValuesType>({
  name,
  children,
  disabled,
  onChecked,
  marginBottom,
  validationMethod,
}: Props<FormValuesType>) {
  const { field, formikContext, isInvalid, t } = useFormField<
    boolean,
    FormValuesType
  >({
    name,
    validationMethod,
    type: 'checkbox',
  });

  const [errorAnimationKey, setErrorAnimationKey] = useState(0);

  useEffect(() => {
    if (isInvalid) {
      setErrorAnimationKey((prev) => prev + 1); // triggers a key update to re-animate
    }
  }, [isInvalid, formikContext.submitCount]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    field.onChange(e);
    onChecked && onChecked(field, formikContext);
  };

  return (
    <CheckboxWrapper
      $marginBottom={marginBottom}
      data-type={isInvalid ? 'invalid-input' : undefined}>
      <CheckboxContainer
        $disabled={disabled}
        key={errorAnimationKey}
        $isError={isInvalid}>
        <input
          disabled={disabled}
          type='checkbox'
          {...field}
          value={field.value?.toString()}
          onChange={handleChange}
        />
        <CheckboxContent>
          <CheckboxIcon />
        </CheckboxContent>
      </CheckboxContainer>
      {children && <TextCheckbox>{children}</TextCheckbox>}

      {isInvalid && (
        <InputMessage
          hasIcon={false}
          type='error'>
          {t('checkbox_error')}
        </InputMessage>
      )}
    </CheckboxWrapper>
  );
}

export default FormCheckbox;
